import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { Button, rem, SettingsSection, Spacer } from 'design-system';
import Text, { lineHeight } from 'design-system/Text';
import { UpgradeWebModal } from 'design-system/UpgradeWebModal';
import { Tier } from 'features/premium/entities';
import { selectTryFor0Text } from 'features/premium/selectors';
import { selectUserTier } from 'features/premium/selectors/tier';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { isWeb } from '../../../constants';
import { selectAutosaveFees, selectInterestInfoForProductId } from '../reducers/selectors';
const CARD_HEIGHT = rem(148 + 16);
const UpgradeComponent = ({ canDismiss, tierRateMap }) => {
    const navigation = useMainStackNavigation();
    const dispatch = useAppDispatch();
    const activeTier = useAppSelector(selectUserTier);
    const userAutosaveFees = useAppSelector(selectAutosaveFees);
    const followingTier = activeTier === Tier.ultimate ? undefined : Tier.ultimate;
    const showComponent = useSharedValue(true);
    const [cardHeight, setCardHeight] = useState(CARD_HEIGHT);
    useEffect(() => {
        if (activeTier === Tier.ultimate) {
            showComponent.value = false;
        }
        else {
            showComponent.value = true;
        }
    }, [activeTier, showComponent]);
    const onTextLayout = useCallback((event) => {
        const lines = event.nativeEvent.lines.length;
        setCardHeight(rem(lines * lineHeight.TextThin_14 + lineHeight.Text_16 + 4 + 12 + 34 + 32 + 16));
    }, []);
    const handleUpgrade = useCallback(() => {
        if (isWeb) {
            UpgradeWebModal.show(Tier.ultimate);
            return;
        }
        navigation.navigateDeprecated('EmmaProModal', {
            tier: Tier.ultimate,
            benefitId: 'potsInterest',
        });
    }, []);
    const animatedStyles = useAnimatedStyle(() => {
        if (!showComponent.value) {
            return { height: withTiming(0) };
        }
        return { height: cardHeight };
    }, [cardHeight, showComponent]);
    const timeout = useRef(undefined);
    const handleDismiss = useCallback(() => {
        showComponent.value = false;
        timeout.current = setTimeout(() => {
            dispatch({
                type: 'HIDE_UPDATE_ACCOUNT_CARD_IN_POTS',
            });
        }, 500);
    }, [showComponent]);
    useEffect(() => () => {
        clearTimeout(timeout.current);
    }, []);
    const tryFor0Text = useAppSelector(selectTryFor0Text);
    if (activeTier && followingTier && tierRateMap && tierRateMap[activeTier] && tierRateMap[followingTier]) {
        return (<Animated.View style={[styles.container, animatedStyles]}>
        <SettingsSection noMarginTop withPadding noMarginBottom style={styles.settingsSection}>
          <Text Text-16>Boost your savings 🚀</Text>
          <Spacer h={4}/>
          <Text TextThin-14 Secondary onTextLayout={onTextLayout}>
            Upgrade your plan and earn up to{' '}
            <Text NumbersThin-14 Secondary>
              {tierRateMap[followingTier]}%
            </Text>{' '}
            AER in your pots{userAutosaveFees === 0 ? '' : ' and avoid Autosave fees'}
          </Text>
          <Spacer h={12}/>
          <View style={styles.row}>
            {canDismiss ? (<>
                <Button title="Upgrade now" brand square style={styles.squareButton} onPress={handleUpgrade}/>
                <Spacer w={8}/>
                <Button title="Dismiss" brandReversed square style={styles.squareButton} onPress={handleDismiss}/>
              </>) : (<Button title={tryFor0Text ?? 'Upgrade now'} brandReversed square style={styles.squareButton} onPress={handleUpgrade}/>)}
          </View>
        </SettingsSection>
      </Animated.View>);
    }
    return null;
};
const UpgradeController = ({ showComponent, canDismiss, productId }) => {
    const activeTier = useAppSelector(selectUserTier);
    const interestInfo = useAppSelector((state) => selectInterestInfoForProductId(state, productId));
    const hideUpdateAccountCardInPots = useAppSelector((store) => store.pots.hideUpdateAccountCardInPots);
    if (showComponent && activeTier !== Tier.ultimate && interestInfo.hasRateUpgrade && interestInfo.tierRateMap) {
        if (!canDismiss || !hideUpdateAccountCardInPots) {
            return <UpgradeComponent canDismiss={canDismiss} tierRateMap={interestInfo.tierRateMap}/>;
        }
    }
    return null;
};
export default memo(UpgradeController);
const styles = StyleSheet.create({
    container: {
        overflow: 'hidden',
    },
    row: {
        flexDirection: 'row',
    },
    squareButton: {
        paddingHorizontal: rem(24),
    },
    settingsSection: {
        marginTop: rem(8),
    },
});
